import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Flex, Text, Button, VStack, HStack, Badge, IconButton, Spinner,
  useDisclosure, Grid, GridItem,
} from '@chakra-ui/react';
import { ArrowBackIcon, AddIcon, EditIcon } from '@chakra-ui/icons';
import { useMICUTheme } from '../lib/micu/MICUThemeProvider';
import { db, getPatient } from '../lib/micu/micuDatabase';
import DeviceAddModal from '../components/micu/DeviceAddModal';
import MedicationAddModal from '../components/micu/MedicationAddModal';
import { formatMinutesAsHHMM, calculateTimeRemaining } from '../lib/micu/calculations';

const DeviceCard = ({ device, onClick, isDark }) => (
  <Box
    p={3}
    bg={isDark ? 'micu.card' : 'micu.card'}
    border="1px solid"
    borderColor="micu.border"
    borderRadius="md"
    cursor="pointer"
    _hover={{ borderColor: 'micu.teal' }}
    onClick={onClick}
  >
    <Text fontWeight="600" fontSize="sm" color="micu.text">{device.deviceType}</Text>
    <Text fontSize="xs" color="micu.textMuted">
      Added {new Date(device.addedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
    </Text>
  </Box>
);

const MedicationCard = ({ med, onClick, isDark }) => {
  const timeRemaining = med.vtbi && med.rate ? calculateTimeRemaining(med.vtbi, med.rate) : null;

  return (
    <Box
      p={3}
      bg={isDark ? 'micu.card' : 'micu.card'}
      border="1px solid"
      borderColor="micu.border"
      borderRadius="md"
      cursor="pointer"
      _hover={{ borderColor: 'micu.teal' }}
      onClick={onClick}
    >
      <Text fontWeight="600" fontSize="sm" color="micu.text">{med.drugName}</Text>
      <HStack spacing={2} mt={1}>
        {med.rate && <Text fontSize="xs" color="micu.textSecondary">{med.rate} {med.rateUnit || 'mL/hr'}</Text>}
        {med.dose && <Text fontSize="xs" color="micu.textSecondary">{med.dose} {med.doseUnit}</Text>}
      </HStack>
      {timeRemaining != null && (
        <HStack spacing={1} mt={1}>
          <Badge
            fontSize="2xs"
            colorScheme={timeRemaining <= (med.alarmMinutes || 15) ? 'red' : 'green'}
            variant="subtle"
          >
            {formatMinutesAsHHMM(timeRemaining)} remaining
          </Badge>
        </HStack>
      )}
    </Box>
  );
};

const MICUCaseDashboard = () => {
  const { id } = useParams();
  const caseId = Number(id);
  const navigate = useNavigate();
  const { isDark } = useMICUTheme();

  const [caseData, setCaseData] = useState(null);
  const [patient, setPatient] = useState(null);
  const [devices, setDevices] = useState([]);
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);

  const deviceModal = useDisclosure();
  const medModal = useDisclosure();

  const loadData = useCallback(async () => {
    setLoading(true);
    const c = await db.cases.get(caseId);
    setCaseData(c);
    const p = await getPatient(caseId);
    setPatient(p);
    const devs = await db.devices.where('caseId').equals(caseId).toArray();
    setDevices(devs);
    const meds = await db.medications.where('caseId').equals(caseId).toArray();
    setMedications(meds);
    setLoading(false);
  }, [caseId]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return <Flex justify="center" align="center" minH="300px"><Spinner color="micu.teal" size="lg" /></Flex>;
  }

  if (!caseData) {
    return <Box p={4}><Text color="micu.red">Case not found.</Text></Box>;
  }

  const patientSummary = patient
    ? [
        patient.gender === 'M' ? 'Male' : patient.gender === 'F' ? 'Female' : '',
        patient.age ? `${patient.age}${patient.ageUnit === 'months' ? 'mo' : 'y'}` : '',
        patient.weightKg ? `${patient.weightKg}kg` : '',
      ].filter(Boolean).join(', ')
    : null;

  return (
    <Box p={4}>
      <HStack spacing={2} mb={2}>
        <IconButton
          icon={<ArrowBackIcon />}
          variant="micuGhost"
          size="sm"
          aria-label="Back to case list"
          onClick={() => navigate('/micu-mobile')}
        />
        <Box flex={1}>
          <Text fontSize="lg" fontWeight="bold" color="micu.text">{caseData.name}</Text>
          {patientSummary && (
            <HStack spacing={1}>
              <Text fontSize="xs" color="micu.textSecondary">{patientSummary}</Text>
              <IconButton
                icon={<EditIcon />}
                variant="micuGhost"
                size="xs"
                aria-label="Edit patient"
                onClick={() => navigate(`/micu-mobile/case/${caseId}/patient`)}
              />
            </HStack>
          )}
          {!patient && (
            <Button variant="link" size="xs" color="micu.teal" onClick={() => navigate(`/micu-mobile/case/${caseId}/patient`)}>
              Add patient info
            </Button>
          )}
        </Box>
      </HStack>

      <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4} mt={4}>
        {/* Devices Column */}
        <GridItem>
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontWeight="600" color="micu.text" fontSize="md">Devices</Text>
            <IconButton
              icon={<AddIcon />}
              variant="micuPrimary"
              size="sm"
              aria-label="Add device"
              onClick={deviceModal.onOpen}
            />
          </Flex>
          <VStack spacing={2} align="stretch">
            {devices.length === 0 ? (
              <Text fontSize="sm" color="micu.textMuted" textAlign="center" py={4}>No devices added</Text>
            ) : (
              devices.map(d => (
                <DeviceCard
                  key={d.id}
                  device={d}
                  isDark={isDark}
                  onClick={() => navigate(`/micu-mobile/case/${caseId}/device/${d.id}`)}
                />
              ))
            )}
          </VStack>
        </GridItem>

        {/* Medications Column */}
        <GridItem>
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontWeight="600" color="micu.text" fontSize="md">Medications</Text>
            <IconButton
              icon={<AddIcon />}
              variant="micuPrimary"
              size="sm"
              aria-label="Add medication"
              onClick={medModal.onOpen}
            />
          </Flex>
          <VStack spacing={2} align="stretch">
            {medications.length === 0 ? (
              <Text fontSize="sm" color="micu.textMuted" textAlign="center" py={4}>No medications added</Text>
            ) : (
              medications.map(m => (
                <MedicationCard
                  key={m.id}
                  med={m}
                  isDark={isDark}
                  onClick={() => navigate(`/micu-mobile/case/${caseId}/med/${m.id}`)}
                />
              ))
            )}
          </VStack>
        </GridItem>
      </Grid>

      <DeviceAddModal isOpen={deviceModal.isOpen} onClose={deviceModal.onClose} caseId={caseId} onAdded={loadData} />
      <MedicationAddModal isOpen={medModal.isOpen} onClose={medModal.onClose} caseId={caseId} onAdded={loadData} />
    </Box>
  );
};

export default MICUCaseDashboard;
